import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon, Loader } from 'semantic-ui-react';
import { useGetQuizQuestionQuery, useUpdateQuizQuestionMutation, useGetProductsQuery } from './productKnowledgeApiSlice';
import CustomDropdown from '../../components/CustomDropdown';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  DetailPageShell, FormField, FormGrid, FormSection, ListNote, SaveBar, ToggleField,
  chip, inputStyle, listCard, stack, textareaStyle, valueBoxStyle,
} from '../../components/listPage';

/** 퀴즈 문제 상세: the question, its answer (객관식 options / 서술형 rubric) and source — one form
 *  on the shared detail layout. 서술형, not 주관식: the quiz's own word. */

const TYPES = [['객관식', 'MC'], ['서술형', 'OE']];
const letter = i => String.fromCharCode(65 + i);
const optionText = (text, i) => (text || '').replace(new RegExp(`^${letter(i)}\\.?\\s*`, 'i'), '');

const MultipleChoiceEditor = ({ meta, onChange }) => {
  const options = meta.options?.length ? meta.options : ['A. ', 'B. ', 'C. ', 'D. '];
  const correct = meta.correct_answer || '';
  const setOption = (i, content) => {
    const next = [...options];
    const old = next[i];
    next[i] = `${letter(i)}. ${content}`;
    // Keep the answer pointing at its option when that option's text changes.
    onChange({ ...meta, options: next, correct_answer: old === correct ? next[i] : correct });
  };
  return (
    <FormSection title="객관식 설정">
      <div style={stack}>
        <FormField label="선택지" hint="동그라미를 눌러 정답을 고르세요.">
          <div style={{ display: 'grid', gap: '8px' }}>
            {options.map((opt, i) => {
              const isCorrect = opt === correct;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    type="button"
                    aria-label={`${letter(i)} 정답으로`}
                    onClick={() => onChange({ ...meta, options, correct_answer: opt })}
                    style={{ width: '24px', height: '24px', flexShrink: 0, borderRadius: '50%', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: isCorrect ? 'none' : '2px solid #d2d2d7', backgroundColor: isCorrect ? '#34C759' : 'transparent' }}
                  >
                    {isCorrect && <Icon name="check" className="centered-icon" style={{ color: 'white', fontSize: '11px' }} />}
                  </button>
                  <span style={{ width: '20px', flexShrink: 0, textAlign: 'center', fontWeight: 600, color: '#1d1d1f' }}>{letter(i)}.</span>
                  <input style={inputStyle} value={optionText(opt, i)} onChange={e => setOption(i, e.target.value)} />
                </div>
              );
            })}
          </div>
        </FormField>
        <FormField label="해설">
          <textarea style={textareaStyle} placeholder="정답에 대한 해설" value={meta.explanation || ''} onChange={e => onChange({ ...meta, explanation: e.target.value })} />
        </FormField>
      </div>
    </FormSection>
  );
};

const OpenEndedEditor = ({ meta, onChange }) => (
  <FormSection title="서술형 설정">
    <div style={stack}>
      <FormField label="모범 답안">
        <textarea style={textareaStyle} placeholder="정답으로 인정할 모범 답안" value={meta.correct_answer || ''} onChange={e => onChange({ ...meta, correct_answer: e.target.value })} />
      </FormField>
      <FormField label="채점 기준" hint="채점 기준을 입력하면 AI가 이를 기준으로 답안을 평가합니다. 비워 두면 모범 답안과 비교합니다.">
        <textarea style={textareaStyle} placeholder="답안을 채점할 기준" value={meta.rubric || ''} onChange={e => onChange({ ...meta, rubric: e.target.value })} />
      </FormField>
    </div>
  </FormSection>
);

const QuizQuestionDetails = () => {
  const { id } = useParams();
  const { isMobile } = useBreakpoint();
  const { data: question, isLoading, error } = useGetQuizQuestionQuery(id);
  const { data: productsData } = useGetProductsQuery({ page_size: 1000 });
  const [updateQuestion, { isLoading: isUpdating }] = useUpdateQuizQuestionMutation();
  const [form, setForm] = useState({ product: '', source_evaluation: '', question_text: '', question_type: 'MC', answer_meta: {}, citation_source: '', citation_url: '', is_active: true });
  const [isDirty, setIsDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (question) {
      setForm({
        product: question.product || '',
        source_evaluation: question.source_evaluation || '',
        question_text: question.question_text || '',
        question_type: question.question_type || 'MC',
        answer_meta: typeof question.answer_meta === 'object' && question.answer_meta ? question.answer_meta : {},
        citation_source: question.citation_source || '',
        citation_url: question.citation_url || '',
        is_active: question.is_active ?? true,
      });
    }
  }, [question]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setIsDirty(true);
    setSaved(false);
    setSaveError(false);
  };
  const handleSave = async () => {
    try {
      await updateQuestion({ id, ...form }).unwrap();
      setIsDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(true);
    }
  };

  const productOptions = (productsData?.results || []).map(p => ({ key: p.id, value: p.id, text: p.name }));
  const productName = productOptions.find(o => o.value === form.product)?.text;

  if (isLoading) return <Loader active />;

  return (
    <DetailPageShell
      wide
      isMobile={isMobile}
      title={productName ? `${productName} 퀴즈` : '퀴즈 문제'}
      subtitle={[form.question_type === 'OE' ? '서술형' : '객관식', question?.user_display_name ? `맞춤 · ${question.user_display_name}` : '공통'].join(' · ')}
      backTo="/admin/quiz-questions"
    >
      {error || !question ? (
        <div style={{ ...listCard, textAlign: 'center', color: '#86868b' }}>퀴즈 문제를 불러오지 못했습니다.</div>
      ) : (
        <div style={stack}>
          {saved && <ListNote tone="green">퀴즈 문제를 저장했습니다.</ListNote>}
          {saveError && <ListNote tone="red">퀴즈 문제를 저장하지 못했습니다.</ListNote>}
          <FormSection title="문제 설정">
            <FormGrid isMobile={isMobile}>
              <FormField label="관련 제품">
                <CustomDropdown searchable placeholder="제품 선택" options={productOptions} value={form.product} onChange={v => set('product', v || '')} icon="box" style={{ width: '100%', height: '42px', borderRadius: '10px' }} />
              </FormField>
              <FormField label="문제 유형">
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '42px' }}>
                  {TYPES.map(([label, value]) => (
                    <button key={value} type="button" onClick={() => set('question_type', value)} style={chip(form.question_type === value)}>{label}</button>
                  ))}
                </div>
              </FormField>
              <FormField label="출처 대화">
                {question.chat_session_id ? (
                  <Link to={`/chatsessions/${question.chat_session_id}`} style={{ ...valueBoxStyle, color: '#007AFF', fontWeight: 600 }}>
                    <Icon name="comments outline" className="centered-icon" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{question.chat_session_str || '대화 보기'}</span>
                  </Link>
                ) : (
                  <div style={{ ...valueBoxStyle, color: '#86868b' }}>연결된 대화 없음</div>
                )}
              </FormField>
              <FormField label="상태"><ToggleField checked={!!form.is_active} onChange={v => set('is_active', v)} /></FormField>
              <FormField label="질문" full>
                <textarea style={{ ...textareaStyle, minHeight: '90px' }} value={form.question_text} onChange={e => set('question_text', e.target.value)} />
              </FormField>
            </FormGrid>
          </FormSection>
          {form.question_type === 'MC'
            ? <MultipleChoiceEditor meta={form.answer_meta} onChange={m => set('answer_meta', m)} />
            : <OpenEndedEditor meta={form.answer_meta} onChange={m => set('answer_meta', m)} />}
          <FormSection title="출처">
            <FormField label="참고 링크">
              {form.citation_url ? (
                <a href={form.citation_url} target="_blank" rel="noopener noreferrer" style={{ ...valueBoxStyle, color: '#007AFF', fontWeight: 600 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.citation_source || form.citation_url}</span>
                  <Icon name="external alternate" className="centered-icon" style={{ fontSize: '12px' }} />
                </a>
              ) : (
                <div style={{ ...valueBoxStyle, color: '#86868b' }}>{form.citation_source || '링크 없음'}</div>
              )}
            </FormField>
          </FormSection>
          <SaveBar onSave={handleSave} disabled={!isDirty} saving={isUpdating} />
        </div>
      )}
    </DetailPageShell>
  );
};

export default QuizQuestionDetails;
